/**
 * @coeldery/chat-composer — 內部零件（拆檔理由：硬規則 B「逐檔 ≤250 行」）
 *
 * 三個細件：① Must-use 兩粒掣（框內底行左）② ▲ 送出掣（框內底行右）③ 已標記 chip 列
 * ⚠️ 一律**自足 inline style ＋ CSS var**（唔靠呼叫方嘅 class）→ 第二個 app 一裝即靚。
 */
import { useTranslation } from 'react-i18next';
import type { PinnedPhrase } from './types.ts';

/** ① Must-use 兩粒（短標籤；完整講法入 aria／title） */
export function MustUseButtons({
  armed,
  onToggle,
  disabled,
}: {
  armed: PinnedPhrase['mode'] | null;
  onToggle: (mode: PinnedPhrase['mode']) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  return (
    <>
      {(['exact', 'meaning'] as const).map((mode) => {
        const active = armed === mode;
        const full = mode === 'exact' ? t('must_use_exact') : t('must_use_meaning');
        return (
          <button
            key={mode}
            type="button"
            data-testid={`must-use-${mode}`}
            aria-pressed={active}
            aria-label={full}
            title={full}
            disabled={disabled}
            onClick={() => onToggle(mode)}
            style={{
              minHeight: '40px',
              padding: '0 14px',
              borderRadius: '999px',
              border: `1.5px solid ${active ? 'var(--color-primary)' : 'var(--color-divider)'}`,
              background: active ? 'var(--color-primary)' : 'var(--color-card)',
              color: active ? 'var(--color-on-primary)' : 'var(--color-text)',
              fontFamily: 'inherit',
              fontSize: 'var(--font-size-small, 15px)',
              fontWeight: 700,
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : 1,
            }}
          >
            {mode === 'exact' ? `🔒 ${t('must_use_exact_short')}` : `≈ ${t('must_use_meaning_short')}`}
          </button>
        );
      })}
    </>
  );
}

/** ② ▲ 送出掣（圓形；只有圖示 → aria／title 講清楚） */
export function SendButton({ label, disabled, onClick }: { label: string; disabled: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      data-testid="composer-send"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      style={{
        marginLeft: 'auto',
        width: '44px',
        height: '44px',
        flex: '0 0 auto',
        borderRadius: '50%',
        border: 'none',
        background: 'var(--color-primary)',
        color: 'var(--color-on-primary)',
        fontFamily: 'inherit',
        fontSize: 'var(--font-size-label, 16px)',
        fontWeight: 700,
        lineHeight: 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      ▲
    </button>
  );
}

/** ③ 已標記「一定要有」嘅 chip 列（框外面） */
export function PinnedChips({
  pinned,
  onUnpin,
}: {
  pinned: PinnedPhrase[];
  onUnpin?: (index: number) => void;
}) {
  const { t } = useTranslation();
  if (pinned.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '6px' }}>
      {pinned.map((p, i) => (
        <span
          key={`${p.text}-${i}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '3px 10px',
            borderRadius: '999px',
            background: 'var(--color-off-bg)',
            color: 'var(--color-text)',
            fontSize: 'var(--font-size-small, 15px)',
          }}
        >
          {p.mode === 'exact' ? '🔒' : '≈'} {p.text}
          {onUnpin ? (
            <button
              type="button"
              aria-label={t('must_use_remove')}
              onClick={() => onUnpin(i)}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'inherit' }}
            >
              ✕
            </button>
          ) : null}
        </span>
      ))}
    </div>
  );
}
